/**
 * Malls API Slice
 * Injects mall endpoints into the base API slice
 */

import { apiSlice } from "../api/apiSlice";
import { Mall } from "../../types/offers";
import { CACHE_TIME } from "../api/apiHelpers";
import { Store } from "../../types/stores";
import { Offer } from "../../types/offers";
import { Event } from "../../types";

// Backend response structure
interface MallsBackendResponse {
  success: boolean;
  data: Mall[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats?: {
    totalMalls: number;
    totalStores: number;
    totalOffers: number;
    totalEvents: number;
  };
}

// Frontend expected structure
export interface MallsResponse {
  malls: Mall[];
  total: number;
  pagination?: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  stats?: {
    totalMalls: number;
    totalStores: number;
    totalOffers: number;
    totalEvents: number;
  };
}

// Malls query parameters
export interface MallsQueryParams {
  page?: number;
  limit?: number;
  searchQuery?: string;
}

// Mall Hub response structure
export interface MallHubResponse {
  mall: Mall;
  stores: Store[];
  offers: Offer[];
  events: Event[];
}

export const mallsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get all malls with pagination support
     * Returns list of malls with pagination info
     */
    getMalls: builder.query<MallsResponse, MallsQueryParams | void>({
      query: (params) => {
        const { page = 1, limit = 4, searchQuery } = params || {};
        return {
          url: `/api/malls`,
          params: {
            page,
            limit,
            search: searchQuery,
          },
        };
      },
      transformResponse: (response: MallsBackendResponse) => {
        // Transform backend response to frontend format
        return {
          malls: response.data || [],
          total: response.pagination?.total || 0,
          pagination: response.pagination,
          stats: response.stats,
        };
      },
      providesTags: (result) => [
        { type: "Malls", id: "LIST" },
        ...(result?.malls.map((mall) => ({
          type: "Malls" as const,
          id: mall.id,
        })) || []),
      ],
      keepUnusedDataFor: CACHE_TIME.VERY_LONG, // 30 minutes - Malls don't change often
    }),

    /**
     * Get mall by ID
     * Returns detailed information about a specific mall
     */
    getMallById: builder.query<Mall, string>({
      query: (mallId) => ({
        url: `/api/malls/${mallId}`,
      }),
      transformResponse: (response: { success: boolean; data: Mall }) => {
        // Transform backend response to frontend format
        return response.data;
      },
      providesTags: (result, error, mallId) => [{ type: "Malls", id: mallId }],
      keepUnusedDataFor: CACHE_TIME.LONG,
    }),

    /**
     * Get mall hub data (stores, offers, events)
     * Returns all data needed for the mall hub page
     */
    getMallHub: builder.query<MallHubResponse, string>({
      query: (mallId) => ({
        url: `/api/malls/${mallId}/hub`,
      }),
      transformResponse: (response: {
        success: boolean;
        data: MallHubResponse;
      }) => {
        return response.data;
      },
      providesTags: (result, error, mallId) => [
        { type: "Malls", id: mallId },
        { type: "Stores", id: "LIST" },
        { type: "Offers", id: "LIST" },
        { type: "Events", id: "LIST" },
      ],
      keepUnusedDataFor: CACHE_TIME.MEDIUM,
    }),
  }),
});

export const { useGetMallsQuery, useGetMallByIdQuery, useGetMallHubQuery } =
  mallsApiSlice;
